import { CustomerDto, FileRecordDto, ModelEventDto } from '@careapp-group/ng-sdk';
import { CustomerFiles, Customers } from '../actions';
import { ORGANISATION_CONTEXT_SWITCH } from '../actions/meta.actions';
import { INITIAL_DATA_LIST_STATE } from '../data-list.state';
import { ReducerStateFactory } from '../state-factory.reducer';
import { CustomersStateInterface } from './customers.state';
import { CareAppListActionFactory } from '../action-factory';

const CustomerUrlActions = new CareAppListActionFactory('ModelEventDto');
const CustomerUrlReducer = new ReducerStateFactory<ModelEventDto>(CustomerUrlActions);

class CustomersReducer extends ReducerStateFactory<CustomerDto> {
  constructor() {
    super(Customers);
  }

  readonly initialState: CustomersStateInterface = {
    ...INITIAL_DATA_LIST_STATE,
    urlHistory: INITIAL_DATA_LIST_STATE,
  };

  reducer(
    state: CustomersStateInterface = this.initialState,
    action: any,
  ): CustomersStateInterface {
    switch (action.type) {
      case Customers.RequestAllPaginated.type: {
        const typedAction: ReturnType<typeof Customers.RequestAllPaginated> = action;
        const paginators = state.paginators || {};
        const paginator = {
          [typedAction.paginatorId]: {
            filter: {},
            loading: true,
            page: { ...INITIAL_DATA_LIST_STATE.page },
            ...paginators[typedAction.paginatorId],
          },
        };

        return { ...state, paginators: { ...paginators, ...paginator } };
      }
      case Customers.ResponseSuccessAllPaginated.type: {
        const typedAction: ReturnType<typeof Customers.ResponseSuccessAllPaginated> = action;
        const legacyAction: ReturnType<typeof Customers.ResponseSuccessAllPaginatedAction> = {
          payload: {
            items: typedAction.items,
            filter: typedAction.filter,
            paginator: typedAction.paginator,
          },
          type: Customers.RESPONSE_SUCCESS_ALL_PAGINATED,
        };
        const superState = super.reducer(state, legacyAction);
        if (!!typedAction.paginator && typedAction.page != null) {
          return {
            ...superState,
            paginators: {
              ...superState.paginators,
              [typedAction.paginator]: {
                ...superState.paginators[typedAction.paginator],
                page: typedAction.page,
                filter: typedAction.filter,
              },
            },
            urlHistory: state.urlHistory,
          };
        }
        return { ...superState, urlHistory: state.urlHistory };
      }
      case ORGANISATION_CONTEXT_SWITCH:
        return { ...state, entities: {}, ids: [], selectedId: null, updatedAt: {} };
      case Customers.REQUEST_SAVE:
      case Customers.RequestAssignCustomerFile.type:
        return { ...state, loadingAll: true };
      case Customers.RESPONSE_FAIL_ALL:
      case Customers.RESPONSE_FAIL_CREATE:
      case Customers.RESPONSE_FAIL_SAVE:
      case Customers.RESPONSE_FAIL:
      case Customers.ResponseFailAssignCustomerFile.type:
        return { ...state, loadingAll: false };
      case Customers.RESPONSE_SUCCESS_SAVE: {
        const ids = Array.from(new Set([...state.ids, action.payload.id]));
        const entities = {
          ...state.entities,
          [action.payload.id]: action.payload,
        };
        return { ...state, loadingAll: false, ids: ids, entities: entities };
      }
      case Customers.RESPONSE_SUCCESS_ADD_CUSTOMER_FILE: {
        const customer = { ...state.entities[action.payload.modelId] };
        customer.files = [...(customer.files || []), action.payload];
        return {
          ...state,
          entities: { ...state.entities, [action.payload.modelId]: customer },
        };
      }
      case Customers.ResponseSuccessAssignCustomerFile.type: {
        const customer = { ...state.entities[action.file.modelId] };
        customer.files = [...(customer.files || []), action.file];
        return {
          ...state,
          entities: { ...state.entities, [action.file.modelId]: customer },
          loadingAll: false,
        };
      }
      case CustomerFiles.RESPONSE_SUCCESS_DESTROY_BY_ID: {
        const customer = { ...state.entities[state.selectedId] };
        customer.files = customer.files.filter((item) => item.id !== action.payload.id);
        return {
          ...state,
          entities: { ...state.entities, [state.selectedId]: customer },
        };
      }
      case CustomerFiles.RESPONSE_SUCCESS_SAVE: {
        const customer = { ...state.entities[state.selectedId] };
        const file: FileRecordDto = action.payload;
        customer.files = [...customer.files];
        const fileIndex = customer.files.findIndex((item) => item.id === file.id);
        customer.files[fileIndex] = file;
        return {
          ...state,
          entities: { ...state.entities, [state.selectedId]: customer },
        };
      }
      case Customers.REQUEST_ACCESS:
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.payload.id]: true },
        };
      case Customers.RESPONSE_SUCCESS_ACCESS:
      case Customers.RESPONSE_FAIL_ACCESS:
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.payload.id]: false },
        };

      case Customers.RequestLinkHistory.type: {
        return {
          ...state,
          urlHistory: CustomerUrlReducer.reducer(
            state.urlHistory,
            CustomerUrlActions.RequestAllPaginatedAction({}, action.urlId, true),
          ),
        };
      }
      case Customers.SuccessLinkHistory.type: {
        return {
          ...state,
          urlHistory: CustomerUrlReducer.reducer(
            state.urlHistory,
            CustomerUrlActions.ResponseSuccessAllPaginatedAction({
              items: action.history,
              paginator: action.urlId,
              filter: {},
            }),
          ),
        };
      }
      case Customers.FailLinkHistory.type: {
        return {
          ...state,
          urlHistory: CustomerUrlReducer.reducer(
            state.urlHistory,
            CustomerUrlActions.ResponseFailAllPaginatedAction(action.error, action.urlId),
          ),
        };
      }
      case Customers.RequestArchiveCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customerId]: true },
        };
      }
      case Customers.SuccessArchiveCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customer.id]: false },
          entities: { ...state.entities, [action.customer.id]: action.customer },
        };
      }
      case Customers.FailArchiveCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customerId]: false },
          error: action.error,
        };
      }
      case Customers.RequestRestoreCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customerId]: true },
        };
      }
      case Customers.SuccessRestoreCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customer.id]: false },
          entities: { ...state.entities, [action.customer.id]: action.customer },
        };
      }
      case Customers.FailRestoreCustomer.type: {
        return {
          ...state,
          loadingEntities: { ...state.loadingEntities, [action.customerId]: false },
          error: action.error,
        };
      }
    }
    return { ...super.reducer(state, action), urlHistory: state.urlHistory };
  }
}

export const state = new CustomersReducer();

export function reducer(...args): CustomersStateInterface {
  return state.reducer.apply(state, args);
}

export const getEntities = (state: any) => state.entities;
